import {Avatar, Button, Checkbox, Modal, Pagination, Popover, Skeleton, Tag, Tooltip} from 'antd'
import {
    course_class_getSummaryReport_Api,
    course_class_student_subject_editResult_Api,
    course_getCourseClassSubjects_Api,
} from 'apis'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {BiSolidEdit} from 'react-icons/bi'
import {PiChecks, PiGraduationCap, PiMedal} from 'react-icons/pi'
import {TbTableOptions} from 'react-icons/tb'
import classNames from 'classnames'
import usePaginationHelper from 'views/AppLocals/hooks/usePaginationHelper'
import {BsChevronDown, BsPencilSquare} from "react-icons/bs";
import {FaUserGraduate} from "react-icons/fa6";

const ResultStatusBadge = ({status, isManual}) => {
    const t = useTranslate()
    if (status === 1) return (
        <span className={classNames(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
            isManual ? 'bg-green-100 text-green-700 ring-1 ring-green-400 ring-offset-0' : 'bg-green-100 text-green-700'
        )}>
            {isManual && <BiSolidEdit size={10}/>}
            {t('passed')}
        </span>
    )
    if (status === 0) return (
        <span className={classNames(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
            isManual ? 'bg-red-100 text-red-600 ring-1 ring-red-400 ring-offset-0' : 'bg-red-100 text-red-600'
        )}>
            {isManual && <BiSolidEdit size={10}/>}
            {t('failed')}
        </span>
    )
    return <span className="text-xs text-color-400 italic">{t('pending')}</span>
}

const OverridePopover = ({resultId, currentStatus, currentEligibility, isManual, onUpdated}) => {
    const t = useTranslate()
    const [open, setOpen] = useState(false)
    const [selStatus, setSelStatus] = useState(currentStatus)
    const [selEligibility, setSelEligibility] = useState(currentEligibility)

    React.useEffect(() => {
        if (open) {
            setSelStatus(currentStatus)
            setSelEligibility(currentEligibility)
        }
    }, [open])

    const {handleAsyncAction: doEditResult, loading} = useAsyncAction({
        apiInfo: course_class_student_subject_editResult_Api,
        onSuccess: (result, data) => {
            setOpen(false)
            const updated = data?.response?.data?.data ?? null
            onUpdated && onUpdated(
                updated && typeof updated === 'object' && updated.result_status !== undefined
                    ? updated
                    : {result_status: selStatus, eligibility_status: selEligibility, is_manual_result: 1}
            )
        },
    })

    const submit = () => {
        if (!resultId) return
        doEditResult({
            result_status: selStatus,
            eligibility_status: selEligibility,
            is_manual_result: 1,
        }, {id: resultId})
    }

    const content = (
        <div className="flex flex-col gap-3 p-1" style={{minWidth: 200}}>
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-color-300 uppercase">
                    {t('eligibility')} ({t('attendances').toLowerCase()})
                </span>
                <div className="flex gap-1.5">
                    {[{v: 1, label: t('eligible'), cls: 'text-green-600 border-green-400'}, {v: 0, label: t('not eligible'), cls: 'text-red-500 border-red-400'}].map(o => (
                        <button
                            key={o.v}
                            onClick={() => setSelEligibility(o.v)}
                            className={classNames(
                                'px-2 py-1 text-xs rounded-md border font-medium transition-colors',
                                selEligibility === o.v ? o.cls + ' font-bold' : 'border-color-200 text-color-400 hover:border-color-300'
                            )}>
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-color-300 uppercase">
                    {t('result')}
                </span>
                <div className="flex gap-1.5">
                    {[{v: 1, label: t('passed'), cls: 'text-green-600 border-green-400'}, {v: 0, label: t('failed'), cls: 'text-red-500 border-red-400'}, {v: -1, label: t('pending'), cls: 'text-color-400 border-color-300'}].map(o => (
                        <button
                            key={o.v}
                            onClick={() => setSelStatus(o.v)}
                            className={classNames(
                                'px-2 py-1 text-xs rounded-md border font-medium transition-colors',
                                selStatus === o.v ? o.cls + ' font-bold' : 'border-color-200 text-color-400 hover:border-color-300'
                            )}>
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="mt-2 pt-2 flex justify-end gap-2  border-t border-color-100">
                <button
                    onClick={() => setOpen(false)}
                    className="px-3 py-1 text-xs text-color-400 hover:text-color-200 transition-colors">
                    {t('cancel')}
                </button>
                <Button
                    type="primary"
                    loading={loading}
                    onClick={submit}
                    className="text-xs rounded-lg">
                    {t('save')}
                </Button>
            </div>
        </div>
    )

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight">
            <Tooltip title={t('override result')}>
                <button
                    style={{ width: 30, height: 30 }}
                    className="ml-1 p-1.5 flex flex-center bg-blue-100 hover:bg-blue-500 rounded-md text-blue-600 hover:text-white transition-colors">
                    <BsPencilSquare size={14}/>
                </button>
            </Tooltip>
        </Popover>
    )
}

const StudentSummaryTableSection = ({
    keyword,
    subjectId,
    courseClassId,
    visibleSumCols,
    attendanceFilter,
    gradeFilter,
}) => {
    const t = useTranslate()
    const [rows, setRows] = useState([])
    const [meta, setMeta] = useState({grades: [], attendances: [], passed_attendance_percentage: 0, passed_score: 0, subject_title: ''})
    const [paginationMeta, setPaginationMeta] = useState({total: 0, current_page: 1, per_page: 20})
    const [currentPage, setCurrentPage] = useState(1)
    const [localRows, setLocalRows] = useState({})
    const [detailStudent, setDetailStudent] = useState(null)

    const {handleAsyncAction: doFetchSummary, loading} = useAsyncAction({
        apiInfo: course_class_getSummaryReport_Api,
        onSuccess: (result, data) => {
            const d = data?.response?.data ?? {}
            setRows(d.data ?? [])
            const m = d.meta ?? {}
            setMeta({
                grades: m.grades ?? [],
                attendances: m.attendances ?? [],
                passed_attendance_percentage: m.passed_attendance_percentage ?? 0,
                passed_score: m.passed_score ?? 0,
                subject_title: m.subject_title ?? '',
            })
            setPaginationMeta(m.pagination ?? {total: 0, current_page: 1, per_page: 20})
            setLocalRows({})
        },
    })

    const fetchPage = useCallback((page) => {
        if (!subjectId || !courseClassId) return
        doFetchSummary({subject_id: subjectId, keyword: keyword || '', page}, {id: courseClassId})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectId, courseClassId, keyword])

    useEffect(() => {
        setCurrentPage(1)
        fetchPage(1)
    }, [fetchPage])

    const handlePageChange = (page) => {
        setCurrentPage(page)
        fetchPage(page)
    }

    const handleStudentUpdated = (studentId, updated) => {
        if (!updated) return
        setLocalRows(prev => ({...prev, [studentId]: {...(prev[studentId] ?? {}), ...updated}}))
    }

    const getEffectiveScore = (gradeData, gradeId) => {
        const d = gradeData?.[gradeId]
        if (!d) return null
        return d.score_2_number ?? d.score_1_number ?? d.score_number
    }

    const grades = meta.grades
    const attendances = meta.attendances
    const passedAttPct = meta.passed_attendance_percentage
    const passedScore = meta.passed_score

    const MAX_GRADE_COLS = 3
    const displayedGrades = grades.slice(0, MAX_GRADE_COLS)
    const hiddenGradeCount = Math.max(0, grades.length - MAX_GRADE_COLS)

    const showClass = visibleSumCols.has('class')
    const showAutoResult = visibleSumCols.has('auto_result')
    const showNotes = visibleSumCols.has('notes')
    const colCount = 1 + (showClass ? 1 : 0) + displayedGrades.length + (hiddenGradeCount > 0 ? 1 : 0) + 1 + (showAutoResult ? 1 : 0) + 2 + 1 + (showNotes ? 1 : 0) + 1

    const displayedRows = useMemo(() => {
        let list = rows.map(r => ({...r, ...(localRows[r.student_id] ?? {})}))
        if (attendanceFilter === 'eligible') {
            list = list.filter(s => passedAttPct > 0 ? (s.cc_percent ?? 0) >= passedAttPct : (s.cc_percent ?? 0) > 0)
        } else if (attendanceFilter === 'not_eligible') {
            list = list.filter(s => passedAttPct > 0 ? (s.cc_percent ?? 0) < passedAttPct : true)
        }
        if (gradeFilter === 'passed') {
            list = list.filter(s => s.result_status === 1)
        } else if (gradeFilter === 'failed') {
            list = list.filter(s => s.result_status === 0)
        } else if (gradeFilter === 'pending') {
            list = list.filter(s => s.result_status == null || s.result_status === -1)
        }
        return list
    }, [rows, localRows, attendanceFilter, gradeFilter, passedAttPct])

    if (loading) return (
        <div className="flex flex-col gap-2 p-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} active paragraph={{rows: 1}} className="m-0"/>)}
        </div>
    )

    return (
        <>
            {/* Table */}
            <div className="overflow-hidden overflow-x-auto shadow-sm">
                <table className="w-full text-xs" style={{minWidth: 600 + grades.length * 80}}>
                    <thead>
                        <tr className="background-100">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-color-300 uppercase whitespace-no-wrap" rowSpan={2}>
                                {t('student')}
                            </th>
                            {showClass && (
                                <th className="px-2 py-2 text-left text-xs font-semibold text-color-300 uppercase whitespace-no-wrap" rowSpan={2}>
                                    {t('class')}
                                </th>
                            )}
                            <th
                                colSpan={displayedGrades.length + (hiddenGradeCount > 0 ? 1 : 0) + 1 + (showAutoResult ? 1 : 0)}
                                className="px-3 py-1.5 text-center text-xs font-bold text-purple-600 uppercase bg-purple-50">
                                <div className="flex items-center justify-center gap-1.5">
                                    <PiGraduationCap size={13}/>
                                    {t('gradebook')}
                                </div>
                            </th>
                            <th
                                colSpan={2}
                                className="px-3 py-1.5 text-center text-xs font-bold text-blue-600 uppercase bg-blue-50">
                                <div className="flex items-center justify-center gap-1.5">
                                    <PiChecks size={13}/>
                                    {t('attendance')}
                                </div>
                            </th>
                            <th
                                colSpan={1 + (showNotes ? 1 : 0)}
                                className="px-3 py-1.5 text-center text-xs font-bold text-green-700 uppercase bg-green-50">
                                <div className="flex items-center justify-center gap-1">
                                    <PiChecks size={13}/>
                                    {t('result')}
                                </div>
                            </th>
                            <th rowSpan={2} className="px-2 py-2"/>
                        </tr>
                        <tr className="background-100 border-b border-color-100 text-2xs">
                            {displayedGrades.map(g => (
                                <th
                                    key={g.id}
                                    className="px-1 py-1.5 text-center text-color-400 font-medium bg-purple-50 whitespace-pre-line max-w-18"
                                    title={g.title}>
                                    <div className="text-center space-x-2">
                                        <span className="font-medium text-color-100 whitespace-pre-line max-lines-3">{g.title}</span>
                                        {g.score_weight > 0 && <span className="font-normal text-purple-400 italic">{`(×${g.score_weight})`}</span>}
                                    </div>
                                </th>
                            ))}
                            {hiddenGradeCount > 0 && (
                                <th className="px-1 py-1.5 text-center bg-purple-50 whitespace-no-wrap">
                                    <Tooltip title={`${hiddenGradeCount} ${t('more columns')}, ${t('see detail')}`}>
                                        <span className="text-purple-300 font-medium cursor-help">{`+${hiddenGradeCount}`}</span>
                                    </Tooltip>
                                </th>
                            )}
                            <th className="px-2 py-1.5 text-center text-purple-600 font-semibold bg-purple-50 whitespace-no-wrap">{t('avg')}</th>
                            {showAutoResult && (
                                <th className="px-2 py-1.5 text-center text-purple-600 font-semibold bg-purple-50 whitespace-no-wrap">{t('auto result')}</th>
                            )}
                            <th className="px-2 py-1.5 text-center text-blue-600 font-semibold bg-blue-50 whitespace-no-wrap">{t('eligibility')}</th>
                            <th className="px-2 py-1.5 text-center text-blue-600 font-semibold bg-blue-50 whitespace-no-wrap">%CC</th>
                            <th className="px-2 py-1.5 text-center text-green-700 font-semibold bg-green-50 whitespace-no-wrap">{t('result status')}</th>
                            {showNotes && (
                                <th className="px-2 py-1.5 text-center text-green-700 font-semibold bg-green-50 whitespace-no-wrap">{t('notes')}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="background divide-y divide-color-50">
                        {displayedRows.length === 0 ? (
                            <tr>
                                <td colSpan={colCount} className="px-4 py-8 text-center text-color-400 text-xs italic">
                                    {t('no data')}
                                </td>
                            </tr>
                        ) : displayedRows.map((student) => {
                            const pct = student.cc_percent ?? 0
                            const isManual = student.is_manual_result === 1
                            const autoScore = student.score_number
                            const scoreOk = passedScore > 0 ? (autoScore ?? 0) >= passedScore : null

                            let pctColor = 'text-color-300'
                            let barColor = 'bg-gray-300'
                            if (passedAttPct > 0) {
                                if (pct >= passedAttPct) { pctColor = 'text-green-600 font-bold'; barColor = 'bg-green-500' }
                                else { pctColor = 'text-red-500 font-bold'; barColor = 'bg-red-400' }
                            } else {
                                if (pct >= 75) { barColor = 'bg-green-500' }
                                else if (pct > 0) { barColor = 'bg-blue-400' }
                            }

                            return (
                                <tr
                                    key={student.student_id}
                                    className={classNames(
                                        'transition-colors',
                                        isManual ? 'bg-orange-50 hover:bg-orange-100' : 'background hover:background-50'
                                    )}>
                                    <td className="px-3 py-2 whitespace-no-wrap">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1">
                                                <span className="font-bold text-gray-800 text-sm tracking-wide">{student.fullname || '-'}</span>
                                                {isManual && (
                                                    <Tooltip title={t('manually overridden')}>
                                                        <BiSolidEdit size={10} className="text-orange-400 flex-shrink-0"/>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {student.student_idcode && (
                                                <span className="text-2xs text-blue-500 font-mono">#{student.student_idcode}</span>
                                            )}
                                        </div>
                                    </td>
                                    {showClass && (
                                        <td className="px-2 py-2 text-xs text-color-400">-</td>
                                    )}
                                    {displayedGrades.map(g => {
                                        const score = getEffectiveScore(student.grade_data, g.id)
                                        return (
                                            <td key={g.id} className="px-2 py-2 text-center bg-purple-50/20">
                                                <span className="text-xs tabular-nums font-medium text-color-200">
                                                    {score != null ? score : '-'}
                                                </span>
                                            </td>
                                        )
                                    })}
                                    {hiddenGradeCount > 0 && (
                                        <td className="px-2 py-2 text-center bg-purple-50/10">
                                            <span className="text-2xs text-color-400">...</span>
                                        </td>
                                    )}
                                    <td className="px-2 py-2 text-center bg-purple-50/30">
                                        <span className={classNames(
                                            'text-xs tabular-nums font-bold',
                                            scoreOk === true ? 'text-green-600' : scoreOk === false ? 'text-red-500' : 'text-color-300'
                                        )}>
                                            {autoScore != null ? autoScore : '-'}
                                        </span>
                                        {passedScore > 0 && (
                                            <div className="text-2xs text-color-300 text-center">{`/ ≥${passedScore}`}</div>
                                        )}
                                    </td>
                                    {showAutoResult && (
                                        <td className="px-2 py-2 text-center bg-purple-50/20">
                                            {student.auto_result_status === 1
                                                ? <Tag color="success" className="text-2xs m-0">{t('passed')}</Tag>
                                                : student.auto_result_status === 0
                                                    ? <Tag color="error" className="text-2xs m-0">{t('failed')}</Tag>
                                                    : <span className="text-2xs text-color-400">-</span>
                                            }
                                        </td>
                                    )}
                                    <td className="px-2 py-2 text-center bg-blue-50/20">
                                        <div className="flex items-center justify-center">
                                            {student.eligibility_status === 1
                                                ? <Tag color="success" className="text-2xs m-0">{t('eligible')}</Tag>
                                                : student.eligibility_status === 0
                                                    ? <Tag color="error" className="text-2xs m-0">{t('not eligible')}</Tag>
                                                    : <span className="text-2xs text-color-400">-</span>
                                            }
                                        </div>
                                    </td>
                                    <td className="px-2 py-2 bg-blue-50/30">
                                        <div className="flex flex-col items-center gap-1" style={{minWidth: 80}}>
                                            <span className={classNames('text-xs tabular-nums font-bold', pctColor)}>
                                                {student.total_count > 0 ? `${pct}%` : '-'}
                                            </span>
                                            {student.total_count > 0 && (
                                                <div className="w-full bg-gray-100 rounded-full" style={{height: 4}}>
                                                    <div
                                                        className={classNames('rounded-full transition-all', barColor)}
                                                        style={{width: `${Math.min(pct, 100)}%`, height: 4}}
                                                    />
                                                </div>
                                            )}
                                            {student.total_count > 0 && (
                                                <span className="text-2xs text-color-400">{`${student.attend_count}/${student.total_count}`}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-center bg-green-50/20">
                                        <div className="flex items-center justify-center gap-1">
                                            <ResultStatusBadge
                                                status={student.result_status}
                                                isManual={isManual}
                                            />
                                            {student.result_status === 1 && (
                                                <Tooltip title={t('certified')}>
                                                    <PiMedal size={14} className="text-yellow-500 flex-shrink-0"/>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                    {showNotes && (
                                        <td className="px-2 py-2 text-center bg-green-50/10">
                                            {student.notes
                                                ? <Tooltip title={student.notes}>
                                                    <span className="text-xs text-color-300 cursor-help truncate block" style={{maxWidth: 80}}>{student.notes}</span>
                                                </Tooltip>
                                                : <span className="text-2xs text-color-400">-</span>
                                            }
                                        </td>
                                    )}
                                    <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-3">
                                            {student.total_count > 0 && (
                                                <Tooltip title={t('view detail')}>
                                                    <button
                                                        style={{ width: 30, height: 30 }}
                                                        className="p-1.5 flex flex-center background-200 hover:bg-secondary-500 rounded-md text-color-300 hover:text-white transition-colors"
                                                        onClick={() => setDetailStudent({row: student, meta})}>
                                                        <BsChevronDown size={16}/>
                                                    </button>
                                                </Tooltip>
                                            )}
                                            {student.result_id && (
                                                <OverridePopover
                                                    resultId={student.result_id}
                                                    currentStatus={student.result_status}
                                                    currentEligibility={student.eligibility_status}
                                                    isManual={isManual}
                                                    onUpdated={(updated) => handleStudentUpdated(student.student_id, updated)}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {(paginationMeta.total ?? 0) > (paginationMeta.per_page ?? 20) && (
                <div className="flex justify-end px-1 pt-1">
                    <Pagination
                        size="small"
                        current={currentPage}
                        total={paginationMeta.total ?? 0}
                        pageSize={paginationMeta.per_page ?? 20}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showTotal={(total) => `${total} ${t('record(s)')}`}
                    />
                </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-2xs text-color-400 italic px-1">
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-sm bg-orange-100 border border-orange-300 flex-shrink-0"/>
                    {t('manually overridden row')}
                </span>
                <span className="flex items-center gap-0.5">
                    <BiSolidEdit size={10} className="text-orange-400"/>
                    {` = ${t('manually overridden')}`}
                </span>
            </div>

            {/* Detail modal */}
            {detailStudent && (
                <Modal
                    width={540}
                    open={!!detailStudent}
                    footer={null}
                    className="custom-modal"
                    title={
                        <div className="text-center font-bold text-color-000 tracking-wide">
                            {t('detail student')}
                        </div>
                    }
                    onCancel={() => setDetailStudent(null)}
                    destroyOnHidden={true}>
                    <div className="flex flex-col gap-4 py-2">
                        <div className="flex items-center gap-3">
                            <Avatar
                                size={36}
                                className="flex-shrink-0 mt-0.5 flex flex-center bg-secondary-50"
                                icon={<FaUserGraduate size={18} className="text-secondary-500"/>}
                            />
                            <div className="flex flex-col">
                                <span className="font-bold text-lg text-color-000">
                                    {detailStudent.row.fullname || '-'}
                                </span>
                                {detailStudent.row.student_idcode && (
                                    <span className="text-xs text-blue-500 font-mono font-normal">{`#${detailStudent.row.student_idcode}`}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide flex items-center gap-1">
                                <PiChecks size={13}/>{t('attendance')}
                            </span>
                            {(detailStudent.meta.attendances ?? []).length === 0 ? (
                                <span className="text-xs text-color-400 italic">{t('no data')}</span>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {(detailStudent.meta.attendances ?? []).map((att, i) => {
                                        const d = (detailStudent.row.attendance_data ?? {})[att.id]
                                        return (
                                            <div key={att.id} className={classNames(
                                                'px-3 py-2 flex items-center rounded-lg border text-xs gap-3',
                                                !d ? 'bg-gray-50 border-color-100'
                                                : d.status === 1 ? 'bg-green-50 border-green-200'
                                                : (d.status === -2 || d.status === -3) ? 'bg-yellow-50 border-yellow-200'
                                                : 'bg-red-50 border-red-200'
                                            )}>
                                                {!d
                                                    ? <span className="text-color-400">-</span>
                                                    : d.status === 1
                                                        ? <span className="text-green-600 font-bold">✓</span>
                                                        : (d.status === -2 || d.status === -3)
                                                            ? <span className="text-yellow-500 font-bold">~</span>
                                                            : <span className="text-red-400 font-bold">✗</span>
                                                }
                                                <span className="font-medium leading-snug tracking-wider">
                                                    {att.title || `#${i + 1}`}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                        {(detailStudent.meta.grades ?? []).length > 0 && (
                            <div className="flex flex-col gap-2">
                                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-1">
                                    <PiGraduationCap size={13}/>{t('gradebook')}
                                </span>
                                <div className="overflow-x-auto ">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="background-100">
                                                {(detailStudent.meta.grades ?? []).map(g => (
                                                    <th key={g.id} className="px-3 py-2 text-center text-color-300 font-semibold">
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <span>{g.title}</span>
                                                            {g.score_weight > 0 && (
                                                                <span className="text-purple-400 font-normal text-2xs">{`×${g.score_weight}`}</span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                                <th className="px-3 py-2 text-center text-purple-600 font-semibold">{t('avg')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="background">
                                                {(detailStudent.meta.grades ?? []).map(g => {
                                                    const gd = (detailStudent.row.grade_data ?? {})[g.id]
                                                    const score = gd ? (gd.score_2_number != null ? gd.score_2_number : (gd.score_1_number != null ? gd.score_1_number : gd.score_number)) : null
                                                    return (
                                                        <td key={g.id} className="px-3 py-3 text-center font-medium text-color-200">
                                                            {score != null ? score : '-'}
                                                        </td>
                                                    )
                                                })}
                                                <td className="px-3 py-3 text-center font-bold text-color-100 whitespace-no-wrap">
                                                    {detailStudent.row.score_number != null ? detailStudent.row.score_number : '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </>
    )
}

export const CourseClassSummaryPanel = ({course_class_id, titleClass}) => {
    const t = useTranslate()
    const [loading, setLoading] = useState(true)
    const [subjectsMeta, setSubjectsMeta] = useState([])
    const [selectedSubjectId, setSelectedSubjectId] = useState(null)
    const [attendanceFilter, setAttendanceFilter] = useState(null)
    const [gradeFilter, setGradeFilter] = useState(null)
    const [visibleSumCols, setVisibleSumCols] = useState(() => new Set(['auto_result']))

    const toggleSumCol = (key) => {
        setVisibleSumCols(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }

    const {handleAsyncAction: doFetchSubjects} = useAsyncAction({
        apiInfo: course_getCourseClassSubjects_Api,
        onSuccess: (result, data) => {
            const items = data?.response?.data?.data ?? []
            const metas = items
                .map(s => ({
                    subject_id: s.subject?.id ?? null,
                    subject_title: s.subject?.title ?? '',
                    passed_attendance_percentage: s.passed_attendance_percentage ?? 0,
                    passed_score: s.passed_score ?? 0,
                }))
                .filter(s => s.subject_id)
            setSubjectsMeta(metas)
            if (metas.length > 0) setSelectedSubjectId(metas[0].subject_id)
            setLoading(false)
        },
        onError: () => setLoading(false),
    })

    useEffect(() => {
        if (!course_class_id) return
        setLoading(true)
        doFetchSubjects({}, {id: course_class_id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_class_id])

    const selectedSubjectMeta = subjectsMeta.find(s => s.subject_id === selectedSubjectId) ?? null

    const renderList = useCallback(({keyword}) => (
        <StudentSummaryTableSection
            keyword={keyword}
            subjectId={selectedSubjectId}
            courseClassId={course_class_id}
            visibleSumCols={visibleSumCols}
            attendanceFilter={attendanceFilter}
            gradeFilter={gradeFilter}
        />
    ), [selectedSubjectId, course_class_id, visibleSumCols, attendanceFilter, gradeFilter])

    const [, {data_widget, handleSearch}] = usePaginationHelper({
        renderList,
        compact: true,
    })

    const [searchInput, setSearchInput] = useState('')

    const passedAttPct = selectedSubjectMeta?.passed_attendance_percentage ?? 0

    if (loading) return (
        <div className="flex flex-col gap-3 p-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} active paragraph={{rows: 1}}/>)}
        </div>
    )

    if (subjectsMeta.length === 0) return (
        <div className="p-6 text-center text-color-400 text-sm italic">{t('no data')}</div>
    )

    return (
        <div className="flex flex-col gap-3">
            {/* Class name */}
            <div className="flex flex-col gap-0.5">
                <span className="font-medium text-xs text-color-300">{`${t('classes')}:`}</span>
                <span className="font-bold text-2xl text-gray-800 tracking-wide">
                    {titleClass}
                </span>
            </div>
            {/* Subject selector */}
            <div className="flex flex-col gap-0.5">
                <span className="font-medium text-xs text-color-300">{`${t('subjects')}:`}</span>
                <div className="flex flex-wrap gap-3">
                    {subjectsMeta.map(sub => {
                        const isActive = sub.subject_id === selectedSubjectId
                        const subPassedAtt = sub.passed_attendance_percentage ?? 0
                        const subPassedScore = sub.passed_score ?? 0
                        return (
                            <button
                                key={sub.subject_id}
                                onClick={() => {
                                    setSelectedSubjectId(sub.subject_id)
                                    setAttendanceFilter(null)
                                    setGradeFilter(null)
                                }}
                                className={classNames(
                                    'px-3 py-1 flex flex-col items-start rounded-lg border transition-colors select-none',
                                    isActive
                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                        : 'border-color-200 text-gray-700 hover:border-indigo-400 hover:text-indigo-500'
                                )}>
                                <span className="font-semibold tracking-wide">
                                    {sub.subject_title}
                                </span>
                                <div className="space-x-3">
                                    {subPassedAtt > 0 && (
                                        <span className={classNames('text-xs font-normal tracking-wider', isActive ? 'text-indigo-200' : 'text-color-300')}>
                                        {`≥${subPassedAtt}%CC`}
                                    </span>
                                    )}
                                    {subPassedScore > 0 && (
                                        <span className={classNames('text-xs font-normal tracking-wider', isActive ? 'text-indigo-200' : 'text-color-300')}>
                                        {`≥${subPassedScore}đ`}
                                    </span>
                                    )}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Filter bar */}
            <div className="p-2.5 flex flex-wrap items-center gap-2 background-100 rounded-xl">
                {/* Search */}
                <div className="flex items-center gap-1.5 px-2 py-1 background rounded-lg border border-color-100 focus-within:border-indigo-400 transition-colors">
                    <svg className="text-color-400 flex-shrink-0" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx={11} cy={11} r={8}/><line x1={21} y1={21} x2={16.65} y2={16.65}/></svg>
                    <input
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSearch(searchInput) }}
                        placeholder={t('search student')}
                        className="text-xs bg-transparent outline-none text-color-100 placeholder-color-400 w-32"
                    />
                    {searchInput && (
                        <button
                            onClick={() => { setSearchInput(''); handleSearch('') }}
                            className="text-color-400 hover:text-color-200 transition-colors leading-none">
                            ×
                        </button>
                    )}
                </div>
                {/* Attendance filter */}
                {passedAttPct > 0 && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-color-400 whitespace-no-wrap">{t('attendance')}:</span>
                        {[
                            {v: null, label: t('all')},
                            {v: 'eligible', label: `≥${passedAttPct}%`, cls: 'text-green-600'},
                            {v: 'not_eligible', label: `<${passedAttPct}%`, cls: 'text-red-500'},
                        ].map(f => (
                            <button
                                key={String(f.v)}
                                onClick={() => setAttendanceFilter(f.v === attendanceFilter ? null : f.v)}
                                className={classNames(
                                    'px-2.5 py-1 text-xs rounded-full border font-medium transition-colors',
                                    attendanceFilter === f.v
                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                        : classNames('background border-color-200 hover:border-secondary-400', f.cls ?? 'text-color-300')
                                )}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                )}
                <div className="w-px h-4 bg-color-100 mx-1"/>
                {/* Result filter */}
                <div className="flex items-center gap-1.5">
                    <span className="text-xs text-color-400 whitespace-no-wrap">{t('result')}:</span>
                    {[
                        {v: null, label: t('all')},
                        {v: 'passed', label: t('passed'), cls: 'text-green-600'},
                        {v: 'failed', label: t('failed'), cls: 'text-red-500'},
                        {v: 'pending', label: t('pending'), cls: 'text-color-400'},
                    ].map(f => (
                        <button
                            key={String(f.v)}
                            onClick={() => setGradeFilter(f.v === gradeFilter ? null : f.v)}
                            className={classNames(
                                'px-2.5 py-1 text-xs rounded-full border font-medium transition-colors',
                                gradeFilter === f.v
                                    ? 'bg-indigo-600 border-indigo-600 text-white'
                                    : classNames('background border-color-200 hover:border-secondary-400', f.cls ?? 'text-color-300')
                            )}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        content={
                            <div className="flex flex-col gap-2 py-1" style={{minWidth: 160}}>
                                {[
                                    {key: 'class', label: t('class')},
                                    {key: 'auto_result', label: t('auto result')},
                                    {key: 'notes', label: t('notes')},
                                ].map(opt => (
                                    <Checkbox
                                        key={opt.key}
                                        checked={visibleSumCols.has(opt.key)}
                                        onChange={() => toggleSumCol(opt.key)}>
                                        <span className="text-sm">{opt.label}</span>
                                    </Checkbox>
                                ))}
                            </div>
                        }>
                        <Tooltip title={t('columns')}>
                            <button className={classNames(
                                'p-1.5 flex flex-center rounded-md border transition-colors',
                                (visibleSumCols.has('class') || visibleSumCols.has('notes'))
                                    ? 'bg-indigo-100 border-indigo-300 text-indigo-600'
                                    : 'background hover:bg-secondary-50 border-color-200 hover:border-secondary-400 text-color-300 hover:text-secondary-600'
                            )}>
                                <TbTableOptions size={14}/>
                            </button>
                        </Tooltip>
                    </Popover>
                </div>
            </div>

            {/* Table + pagination */}
            {data_widget}
        </div>
    )
}
